import React, { useState, useMemo } from 'react';
import { 
    Card, 
    Input, 
    Button, 
    Space, 
    Typography, 
    Checkbox, 
    Badge, 
    Tooltip,
    Divider,
    Select,
    Empty
} from 'antd';
import { 
    RightOutlined, 
    LeftOutlined, 
    SearchOutlined, 
    DoubleRightOutlined, 
    DoubleLeftOutlined,
    FilterOutlined
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { NubPermissionDTO } from '../types';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

interface PermissionSelectorProps {
    permissions: NubPermissionDTO[];
    selectedPermissions: string[];
    permissionCategories: string[];
    getPermissionsByCategory: (category: string) => NubPermissionDTO[];
    onPermissionChange: (permissions: string[]) => void;
    disabled?: boolean;
}

export const PermissionSelector: React.FC<PermissionSelectorProps> = ({
    permissions,
    selectedPermissions,
    permissionCategories,
    getPermissionsByCategory,
    onPermissionChange,
    disabled = false
}) => {
    const { dark } = useTheme();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
    const [selectedAvailable, setSelectedAvailable] = useState<string[]>([]);
    const [selectedAssigned, setSelectedAssigned] = useState<string[]>([]);

    // Get available permissions (not selected)
    const availablePermissions = useMemo(() => {
        // @ts-ignore
        return permissions.filter(p => !selectedPermissions.includes(p.code));
    }, [permissions, selectedPermissions]);

    // Get selected permissions as objects
    const assignedPermissions = useMemo(() => {
        // @ts-ignore
        return permissions.filter(p => selectedPermissions.includes(p.code));
    }, [permissions, selectedPermissions]);

    // Filter available permissions based on search and category
    const filteredAvailablePermissions = useMemo(() => {
        let filtered = availablePermissions;

        // Filter by category
        if (selectedCategory !== 'ALL') {
            filtered = filtered.filter(p => p.category === selectedCategory);
        }

        // Filter by search term
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(p =>
                // @ts-ignore
                p.name.toLowerCase().includes(searchLower) ||
                // @ts-ignore
                p.code.toLowerCase().includes(searchLower) ||
                (p.description && p.description.toLowerCase().includes(searchLower))
            );
        }

        return filtered;
    }, [availablePermissions, selectedCategory, searchTerm]);

    // Filter assigned permissions based on search
    const filteredAssignedPermissions = useMemo(() => {
        if (!searchTerm) return assignedPermissions;
        
        const searchLower = searchTerm.toLowerCase();
        return assignedPermissions.filter(p =>
            // @ts-ignore
            p.name.toLowerCase().includes(searchLower) ||
            // @ts-ignore
            p.code.toLowerCase().includes(searchLower) ||
            (p.description && p.description.toLowerCase().includes(searchLower))
        );
    }, [assignedPermissions, searchTerm]);

    // Move selected available permissions to assigned
    const handleMoveToAssigned = () => {
        const newSelected = [...selectedPermissions, ...selectedAvailable];
        onPermissionChange(newSelected);
        setSelectedAvailable([]);
    };

    // Move all available permissions to assigned
    const handleMoveAllToAssigned = () => {
        const allAvailableCodes = filteredAvailablePermissions.map(p => p.code);
        const newSelected = [...selectedPermissions, ...allAvailableCodes];
        // @ts-ignore
        onPermissionChange(newSelected);
        setSelectedAvailable([]);
    };

    // Move selected assigned permissions to available
    const handleMoveToAvailable = () => {
        const newSelected = selectedPermissions.filter(code => !selectedAssigned.includes(code));
        onPermissionChange(newSelected);
        setSelectedAssigned([]);
    };

    // Move all assigned permissions to available
    const handleMoveAllToAvailable = () => {
        onPermissionChange([]);
        setSelectedAssigned([]);
    };

    // Get category color
    const getCategoryColor = (category: string) => {
        const colors = {
            'GENERAL': '#52c41a',
            'LOANS': '#1890ff',
            'ACCOUNTS': '#722ed1',
            'APPROVALS': '#fa8c16',
            'PAYMENTS': '#eb2f96',
            'USERS': '#13c2c2',
            'SETTINGS': '#faad14',
            'FLOWS': '#f5222d'
        };
        return colors[category as keyof typeof colors] || '#666';
    };

    // Render permission item
    const renderPermissionItem = (permission: NubPermissionDTO, isSelected: boolean, onSelect: (code: string, checked: boolean) => void) => (
        <div
            key={permission.code}
            className={`p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                isSelected 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            // @ts-ignore
            onClick={() => !disabled && onSelect(permission.code, !isSelected)}
        >
            <div className="flex items-start space-x-3">
                <Checkbox
                    checked={isSelected}
                    // @ts-ignore
                    onChange={(e) => !disabled && onSelect(permission.code, e.target.checked)}
                    disabled={disabled}
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                        <Text strong className="text-sm">
                            {permission.name}
                        </Text>
                        <Badge 
                            color={
                                // @ts-ignore
                            getCategoryColor(permission.category)}
                            text={permission.category}
                            style={{ fontSize: '10px' }}
                        />
                    </div>
                    <Text type="secondary" className="text-xs block">
                        {permission.code}
                    </Text>
                    {permission.description && (
                        <Text type="secondary" className="text-xs block mt-1">
                            {permission.description}
                        </Text>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-4">
            {/* Search and Filter Controls */}
            <div className="flex space-x-4">
                <Search
                    placeholder="Search permissions..."
                    allowClear
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    prefix={<SearchOutlined />}
                    className="flex-1"
                />
                <Select
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                    style={{ width: 150 }}
                    suffixIcon={<FilterOutlined />}
                >
                    <Option value="ALL">All Categories</Option>
                    {permissionCategories.map(category => (
                        <Option key={category} value={category}>
                            <Badge color={getCategoryColor(category)} text={category} />
                        </Option>
                    ))}
                </Select>
            </div>

            {/* Permission Selector Layout */}
            <div className="grid grid-cols-5 gap-4 h-[600px]">
                {/* Available Permissions (Left Panel) */}
                <div className="col-span-2">
                    <Card
                        title={
                            <div className="flex justify-between items-center">
                                <span>Available Permissions</span>
                                <Badge count={filteredAvailablePermissions.length} showZero />
                            </div>
                        }
                        size="small"
                        className={`h-full ${dark ? 'bg-gray-800' : ''}`}
                        bodyStyle={{ padding: '12px', height: 'calc(100% - 57px)', overflowY: 'auto', maxHeight: '520px' }}
                    >
                        {filteredAvailablePermissions.length === 0 ? (
                            <Empty 
                                description="No available permissions"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        ) : (
                            <div className="space-y-2">
                                {filteredAvailablePermissions.map(permission =>
                                    renderPermissionItem(
                                        permission,
                                        // @ts-ignore
                                        selectedAvailable.includes(permission.code),
                                        (code, checked) => {
                                            if (checked) {
                                                setSelectedAvailable([...selectedAvailable, code]);
                                            } else {
                                                setSelectedAvailable(selectedAvailable.filter(c => c !== code));
                                            }
                                        }
                                    )
                                )}
                            </div>
                        )}
                    </Card>
                </div>

                {/* Move Buttons (Center Panel) */}
                <div className="col-span-1 flex flex-col justify-center items-center space-y-3">
                    <Tooltip title="Move selected to assigned">
                        <Button
                            type="primary"
                            icon={<RightOutlined />}
                            onClick={handleMoveToAssigned}
                            disabled={disabled || selectedAvailable.length === 0}
                            size="large"
                        />
                    </Tooltip>
                    
                    <Tooltip title="Move all available to assigned">
                        <Button
                            type="default"
                            icon={<DoubleRightOutlined />}
                            onClick={handleMoveAllToAssigned}
                            disabled={disabled || filteredAvailablePermissions.length === 0}
                        />
                    </Tooltip>

                    <Divider />

                    <Tooltip title="Move all assigned to available">
                        <Button
                            type="default"
                            icon={<DoubleLeftOutlined />}
                            onClick={handleMoveAllToAvailable}
                            disabled={disabled || assignedPermissions.length === 0}
                        />
                    </Tooltip>

                    <Tooltip title="Move selected to available">
                        <Button
                            type="primary"
                            icon={<LeftOutlined />}
                            onClick={handleMoveToAvailable}
                            disabled={disabled || selectedAssigned.length === 0}
                            size="large"
                        />
                    </Tooltip>
                </div>

                {/* Assigned Permissions (Right Panel) */}
                <div className="col-span-2">
                    <Card
                        title={
                            <div className="flex justify-between items-center">
                                <span>Assigned Permissions</span>
                                <Badge count={assignedPermissions.length} showZero />
                            </div>
                        }
                        size="small"
                        className={`h-full ${dark ? 'bg-gray-800' : ''}`}
                        bodyStyle={{ padding: '12px', height: 'calc(100% - 57px)', overflowY: 'auto', maxHeight: '520px' }}
                    >
                        {filteredAssignedPermissions.length === 0 ? (
                            <Empty 
                                description="No assigned permissions"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        ) : (
                            <div className="space-y-2">
                                {filteredAssignedPermissions.map(permission =>
                                    renderPermissionItem(
                                        permission,
                                        // @ts-ignore
                                        selectedAssigned.includes(permission.code),
                                        (code, checked) => {
                                            if (checked) {
                                                setSelectedAssigned([...selectedAssigned, code]);
                                            } else {
                                                setSelectedAssigned(selectedAssigned.filter(c => c !== code));
                                            }
                                        }
                                    )
                                )}
                            </div>
                        )}
                    </Card>
                </div>
            </div>

            {/* Summary */}
            <div className="flex justify-between items-center text-sm text-gray-500">
                <span>
                    {selectedAvailable.length > 0 && `${selectedAvailable.length} available selected`}
                </span>
                <span>
                    Total: {assignedPermissions.length} permissions assigned
                </span>
                <span>
                    {selectedAssigned.length > 0 && `${selectedAssigned.length} assigned selected`}
                </span>
            </div>
        </div>
    );
};
